import { queryFooter, queryNavbar, queryPageBySlug, querySectionsBySystemIds } from '@/clients/queries';
import { chunk } from '@/utils/arrays';
import { GetStaticProps } from 'next';
import * as React from 'react';
import config from '@/config/config';
import { FooterQuery, NavbarQuery, PageBySlugQuery, PageSectionsBySystemIdQuery } from 'types/generated';
import { centeredMainGridClasses } from '@/styles/grid';
import { Label } from 'components/src/typography';
import PageContainer from '@/components/PageContainer';
import { getOr } from '@/utils/options';
import ServiceMap from '@/components/ServiceMap';
import { getClient } from '@/utils/contentful';
import { getToken } from '@/data/sitewatch/token-manager';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import { Log } from '@/logging/datadog';

type Props = {
  page: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  radioSchedules: Record<string, RadioSchedule[]>;
  orderedIds: Array<string>;
  token: string;
};

const ThjonustusvaediPage = ({ navbar, footer, page, sections, radioSchedules, orderedIds, token }: Props) => {
  const pageCollection = page.pageCollection?.items[0];

  if (!pageCollection) {
    return (
      <div className={`${centeredMainGridClasses} mt-16 text-center`}>
        <Label>{'404 - /reiki page not found'}</Label>
      </div>
    );
  }

  return (
    <PageContainer
      title={getOr(pageCollection.title, 'Sýn')}
      metaTags={pageCollection.metaTags}
      contactFormSetting={pageCollection.contactFormSetting}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <div className="mt-128 justify-items-center">
        <ServiceMap sections={sections} orderedIds={orderedIds} token={token} />
      </div>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = 'thjonustusvaedi';
  const inPreview = getOr(context.draftMode, false);
  try {
    const client = getClient(inPreview);

    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;
    const token = await getToken();

    if (!token) {
      Log.error('Failed to obtain SiteWatch token');
    }

    if (!contentfulPage.pageCollection) {
      Log.error(`no page collection found in graphql response when fetching page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    if (!contentfulPage.pageCollection.items) {
      Log.error(`no items array set in page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      Log.error(`no page component found in graphql response for slug ${slug}`);
      return {
        notFound: true,
      };
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    return {
      props: {
        navbar: (await queryNavbar(client)).data,
        footer: (await queryFooter(client)).data,
        page: contentfulPage,
        sections: await Promise.all(chunkedSections),
        radioSchedules: await getRadioSchedules(),
        orderedIds: sectionIds,
        token: token || '',
      },
      revalidate: 6900, // every 1 hour and 55 minutes
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default ThjonustusvaediPage;
